/**
 * BHG shared TypeScript types – used by frontend and backend.
 * Single source of truth for API contracts and data structures.
 * @see docs/02-SHARED-TYPES.md
 */

// ============================================================================
// USER TYPES
// ============================================================================

export type UserRole =
  | 'super-admin'
  | 'admin'
  | 'board-member'
  | 'tutor'
  | 'parent';

export type UserStatus =
  | 'pending-verification'
  | 'pending-approval'
  | 'active'
  | 'inactive'
  | 'rejected'
  | 'suspended'
  | 'deleted';

/** How a tutor/staff member accepts class payments from families. */
export type ClassPaymentMethod = 'venmo' | 'paypal' | 'cash';

export interface User {
  userId: string;
  email: string;
  name: string;
  /** Built-in role key (e.g. "admin") or custom role id (e.g. "role_<uuid>"). */
  role: string;
  status: UserStatus;
  phoneNumber: string;
  phoneVerified: boolean;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  /** How 2FA is performed: TOTP (authenticator app) or SMS. Omitted or 'totp' for existing users. */
  twoFactorMethod?: 'totp' | 'sms';
  profileImageUrl?: string;
  invitedBy?: string;
  vouchedBy?: string;
  agreedToTerms: boolean;
  agreedToTermsAt?: number;
  requiredDocumentsReviewed: string[];
  createdAt: number;
  updatedAt: number;
  lastLoginAt?: number;
  /** Set when an admin rejects registration; user no longer appears in approval queue. */
  rejectedAt?: number;
  rejectionReason?: string;
  /** User ID of the admin who rejected (audit trail). */
  rejectedByUserId?: string;
  /** Resolved name of the rejector (for display); set by list-users when available. */
  rejectedByName?: string;
  /** Set when an admin approves registration (audit trail). */
  approvedAt?: number;
  approvedByUserId?: string;
  /** Resolved permission keys for this user's role (built-in or custom). Set by frontend after GET /users/:userId. */
  effectivePermissions?: string[];
  /** When true, user is not required to complete family registration and is excluded from registration counts. */
  familyRegistrationExempt?: boolean;
  /**
   * When true, this parent is treated as a returning family for enrollment windows
   * (returning_families audience), even without prior-semester enrollment data in the system.
   * Set manually or via the returning-families import (used to bootstrap the first semester).
   */
  returningFamily?: boolean;
  /** Home address – formatted line for autocomplete display. */
  homeAddress?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  /** Per-event notification delivery preferences (Phase 6). */
  notificationPreferences?: UserNotificationPreferences;
  /**
   * Enrollment window reminder/open already delivered (survives "Clear all" on notifications).
   * Keys: `${semesterId}:${windowId}:reminder` | `:open` → sent-at ms.
   */
  enrollmentWindowNotificationsSent?: Record<string, number>;
  /** Class submission deadline reminders already delivered (survives notification clear-all).
   * Keys: `${semesterId}:${reminderId}` → sent-at ms.
   */
  submissionDeadlineNotificationsSent?: Record<string, number>;
  /**
   * Thursday attendance reminders already delivered (survives notification clear-all).
   * Keys: `${semesterId}:${dateKey}` → sent-at ms.
   */
  thursdayAttendanceNotificationsSent?: Record<string, number>;
  /**
   * Full-class waitlist close reminders already delivered (survives notification clear-all).
   * Keys: `${semesterId}:${classId}` → sent-at ms.
   */
  waitlistCloseRemindersSent?: Record<string, number>;
  /** New email awaiting confirmation (not applied until link is used). */
  pendingEmail?: string;
  /** Unix ms when the pending email confirmation link expires. */
  pendingEmailExpiresAt?: number;
  /**
   * Preferred way families pay this user for class fees (tutors and staff who teach).
   * Required before creating a class when role has create-class access.
   */
  preferredClassPaymentMethod?: ClassPaymentMethod;
  /**
   * Venmo username or PayPal.me link name (normalized, no @ or URL).
   * Not used when preferredClassPaymentMethod is cash.
   */
  classPaymentHandle?: string;
  /** Public tutor profile — education background (optional). */
  tutorEducation?: string;
  /** Public tutor profile — teaching experience (optional). */
  tutorExperience?: string;
  /** Public tutor profile — biography (optional). */
  tutorBiography?: string;
  /** Admin/super-admin only — preset for Role at BHG on public profile. */
  tutorBhgRoleAtBhg?: 'director' | 'board-member' | 'other';
  /** When tutorBhgRoleAtBhg is other — short custom label on public profile. */
  tutorBhgRoleAtBhgOther?: string;
}

/** Delivery mode for a notification category. */
export type NotificationDeliveryMode = 'both' | 'in_app' | 'email' | 'off';

/** Keys align with NOTIFICATIONS-AND-EMAIL-PLAN event types. */
export interface UserNotificationPreferences {
  accountApproved?: NotificationDeliveryMode;
  classReviewUpdates?: NotificationDeliveryMode;
  semesterInvite?: NotificationDeliveryMode;
  newUserApproval?: NotificationDeliveryMode;
  /**
   * Board/admin/super-admin: family completed registration with no home church
   * (or asked to learn about visiting Bethlehem).
   */
  noHomeChurchFollowUp?: NotificationDeliveryMode;
  submissionDeadline?: NotificationDeliveryMode;
  /** Thursday attendance reminders for tutors with approved classes in session. */
  thursdayAttendanceReminders?: NotificationDeliveryMode;
  /**
   * When thursdayAttendanceReminders is not off, also send the Thursday attendance SMS.
   * Omitted or true = send when phone is verified and SMS consent exists; false = never.
   */
  thursdayAttendanceSms?: boolean;
  /** Parent/guardian: yearly family registration fee marked paid by an admin. */
  familyRegistrationPaid?: NotificationDeliveryMode;
  /**
   * Parent/guardian: child enrolled or removed from a class
   * (enrollment confirmation includes tutor payment instructions).
   */
  parentChildEnrolled?: NotificationDeliveryMode;
  /** Parent/guardian: tutor recorded a class enrollment payment or applied a discount. */
  parentEnrollmentPayment?: NotificationDeliveryMode;
  /** Parent/guardian: waitlist join, seat offered from waitlist, or waitlist closed. */
  parentWaitlistUpdates?: NotificationDeliveryMode;
  /** Tutor: enrollments, unenrollments, and class capacity on their roster. */
  tutorEnrollmentUpdates?: NotificationDeliveryMode;
  /**
   * Tutor/staff who teach: semester started, class is full, waitlist still on.
   * In-app cannot be turned off; email is optional (both or in_app only).
   */
  tutorWaitlistCloseReminder?: NotificationDeliveryMode;
  /** Parent/guardian: new documents uploaded for a class their child is in. */
  classResourceUpdates?: NotificationDeliveryMode;
  /** Co-op-wide announcements from admins. */
  siteAnnouncements?: NotificationDeliveryMode;
  /** When your semester enrollment window opens and advance reminders. */
  enrollmentWindowUpdates?: NotificationDeliveryMode;
}

/** Co-op-wide document (policies, plans) managed by super admin. */
export interface OrganizationDocument {
  documentId: string;
  title: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  /** Shown on landing Resources section when file is uploaded. */
  showOnLanding: boolean;
  /** Shown as required checkbox on family registration. */
  requiresAgreementOnRegistration: boolean;
  /**
   * At most one document may have this set. When true (and a file is uploaded),
   * a "View schedule" button appears on the Explore Classes / Enroll page.
   * Setting this on one document clears it on all others.
   */
  showOnEnrollPage?: boolean;
  fileKey?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  uploadedAt?: number;
  createdAt: number;
  updatedAt: number;
}

/** Public-facing subset (no admin fields). */
export interface PublicOrganizationDocument {
  documentId: string;
  title: string;
  description?: string;
  sortOrder: number;
  showOnLanding: boolean;
  requiresAgreementOnRegistration: boolean;
  /** True when this document is the active enroll-page schedule PDF. */
  showOnEnrollPage: boolean;
  hasFile: boolean;
}

export interface ListPublicOrganizationDocumentsResponse {
  documents: PublicOrganizationDocument[];
}

export interface ListOrganizationDocumentsResponse {
  documents: OrganizationDocument[];
}

export interface CreateOrganizationDocumentRequest {
  title: string;
  description?: string;
  sortOrder?: number;
  showOnLanding?: boolean;
  requiresAgreementOnRegistration?: boolean;
  showOnEnrollPage?: boolean;
}

export interface UpdateOrganizationDocumentRequest {
  title?: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
  showOnLanding?: boolean;
  requiresAgreementOnRegistration?: boolean;
  showOnEnrollPage?: boolean;
}

/** Global site settings (single row, PK = settingsKey). */
/** Co-op password rules (configurable in App settings → Security). */
export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
}

export interface SiteSettings {
  settingsKey: string;
  showFamilyDiscountClassesOnEnroll: boolean;
  /** Yearly family registration fee in USD cents (default $30 = 3000). */
  familyRegistrationFeeCents: number;
  /**
   * Nonrefundable per-class deposit in USD cents (default $20 = 2000).
   * Paid to the tutor to secure a seat after enrollment.
   */
  classDepositCents: number;
  passwordPolicy: PasswordPolicy;
  /** When true, landing page is hidden, enrollment is suspended, and parent login is blocked. Admins may still use the app. */
  siteMaintenanceMode?: boolean;
  /** Optional message on the maintenance page. */
  maintenanceMessage?: string;
  /** Contact email shown on maintenance page. */
  maintenanceSupportEmail?: string;
  /**
   * Director contact email — footer, Terms, Privacy Policy, and legal/privacy inquiries.
   * Also used on the maintenance page when maintenance support email is empty.
   */
  directorEmail?: string;
  /** When false, POST /auth/register is rejected and public Register links are hidden. Default: allowed. */
  allowNewRegistrations?: boolean;
  /** When registrations are closed, show a notice on the log in page. */
  showRegistrationClosedNotice?: boolean;
  /** Custom copy for the log in notice; default used when empty. */
  registrationClosedMessage?: string;
  /** S3 key for landing page “Meet Our Board” photo. */
  boardPhotoKey?: string;
  /** Names caption under the board photo (left-to-right). */
  boardPhotoCaption?: string;
  /**
   * Address autocomplete provider for family registration / account address fields.
   * Live switch — no redeploy. Keys still come from the frontend build env.
   */
  addressAutocompleteProvider?: 'geoapify' | 'google';
  updatedAt: number;
  updatedByUserId?: string;
}

export interface GetSiteSettingsResponse {
  settings: SiteSettings;
}

export interface UpdateSiteSettingsRequest {
  showFamilyDiscountClassesOnEnroll?: boolean;
  familyRegistrationFeeCents?: number;
  classDepositCents?: number;
  passwordPolicy?: Partial<PasswordPolicy>;
  siteMaintenanceMode?: boolean;
  maintenanceMessage?: string;
  maintenanceSupportEmail?: string;
  directorEmail?: string;
  allowNewRegistrations?: boolean;
  showRegistrationClosedNotice?: boolean;
  registrationClosedMessage?: string;
  boardPhotoCaption?: string;
  /** Pass null to clear boardPhotoKey. */
  boardPhotoKey?: string | null;
  addressAutocompleteProvider?: 'geoapify' | 'google';
}

/** Persisted site announcement shown on the dashboard until endDate (YYYY-MM-DD, inclusive). */
export interface SiteAnnouncement {
  announcementId: string;
  title: string;
  message: string;
  /** Tabler icon key (see announcementIcons allowlist). */
  iconType: string;
  /** Last calendar day to show on the dashboard (YYYY-MM-DD, UTC). */
  endDate: string;
  /** When true, also shown at the top of the public landing page (no sign-in required). */
  showOnLandingPage?: boolean;
  createdAt: number;
  createdByUserId: string;
}

export interface SendSiteAnnouncementRequest {
  title: string;
  message: string;
  sendEmail?: boolean;
  endDate: string;
  iconType?: string;
  /** When true, also shown at the top of the public landing page. */
  showOnLandingPage?: boolean;
}

export interface SendSiteAnnouncementResponse {
  recipientCount: number;
  emailsSent: number;
  announcement: SiteAnnouncement;
  message: string;
  /** Set when the banner was saved but email and/or audit work failed afterward. */
  deliveryWarning?: string;
}

export interface ListSiteAnnouncementsResponse {
  announcements: SiteAnnouncement[];
  active: SiteAnnouncement[];
}

export interface DeleteSiteAnnouncementResponse {
  message: string;
}

/** PUT /users/me – self-service profile (not role/status). */
export interface UpdateMeRequest {
  name?: string;
  phoneNumber?: string;
  homeAddress?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  notificationPreferences?: UserNotificationPreferences;
  preferredClassPaymentMethod?: ClassPaymentMethod;
  classPaymentHandle?: string | null;
  tutorEducation?: string | null;
  tutorExperience?: string | null;
  tutorBiography?: string | null;
  tutorBhgRoleAtBhg?: 'director' | 'board-member' | 'other' | null;
  tutorBhgRoleAtBhgOther?: string | null;
}

export interface UpdateMeResponse {
  user: User;
  message: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
}

/** POST /users/me/change-2fa/send-code */
export interface Change2FASendCodeRequest {
  currentPassword: string;
}

export interface Change2FASendCodeResponse {
  message: string;
  smsCode?: string;
}

/** POST /users/me/change-2fa/verify-current */
export interface Change2FAVerifyCurrentRequest {
  currentPassword: string;
  code: string;
}

export interface Change2FAVerifyCurrentResponse {
  message: string;
  changeToken: string;
  currentMethod: 'totp' | 'sms';
}

/** POST /users/me/change-2fa/setup */
export interface Change2FASetupRequest {
  changeToken: string;
  newMethod: 'totp' | 'sms';
}

/** POST /users/me/change-2fa/confirm */
export interface Change2FAConfirmRequest {
  changeToken: string;
  code: string;
}

export interface Change2FAConfirmResponse {
  message: string;
  backupCodes: string[];
  user: User;
}

/** POST /users/me/request-email-change */
export interface RequestEmailChangeRequest {
  newEmail: string;
  currentPassword: string;
}

export interface RequestEmailChangeResponse {
  message: string;
  pendingEmail: string;
  pendingEmailExpiresAt: number;
  user: User;
  /** Local dev only – confirmation link for testing without SES. */
  confirmationUrl?: string;
  confirmToken?: string;
}

/** POST /users/me/cancel-email-change */
export interface CancelEmailChangeResponse {
  message: string;
  user: User;
}

/** POST /auth/confirm-email-change */
export interface ConfirmEmailChangeRequest {
  token: string;
}

export interface ConfirmEmailChangeResponse {
  message: string;
  user: User;
}

export interface Child {
  childId: string;
  parentUserId: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gradeLevel: string;
  createdAt: number;
  updatedAt: number;
  isDeleted?: boolean;
  deletedAt?: number;
  /**
   * When true, this row is the auto-created Adult learner profile for the signed-in parent
   * (used to enroll in Adult-level classes without a separate Family step).
   */
  isSelfLearner?: boolean;
  /** Known allergies or medical alerts for tutors (optional). */
  allergies?: string;
  /** Additional notes for tutors (optional). */
  specialComments?: string;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface ContactFormSubmission {
  name: string;
  email: string;
  phone?: string;
  message: string;
  honeypot?: string;
}

export interface ScheduleVisitSubmission {
  name: string;
  email: string;
  phone: string;
  /** Preferred Thursday YYYY-MM-DD; omit when no active semester dates are available. */
  visitDate?: string;
  honeypot?: string;
}

// ============================================================================
// AUTH REQUEST/RESPONSE (Phase 1)
// ============================================================================

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phoneNumber: string;
  /** Must be true — user accepted Terms of Service and Privacy Policy at registration. */
  agreedToTerms: boolean;
  /** Must be true — explicit opt-in to SMS verification codes (AWS toll-free compliance). */
  agreedToSmsConsent: boolean;
  /** Omitted for self-registration (default: parent). Set only when completing an invitation. */
  role?: 'parent' | 'tutor';
  vouchedByEmail?: string;
  honeypot?: string;
}

/** Request body for POST /users/invite. Inviter chooses role (built-in key or custom role id). */
export interface InviteUserRequest {
  email: string;
  name?: string;
  role: string;
  /** When true, invitee skips annual family registration after account setup. */
  familyRegistrationExempt?: boolean;
}

/** Response from POST /users/invite. In local dev, inviteLink and token may be present. */
export interface InviteUserResponse {
  message: string;
  inviteId: string;
  inviteLink?: string;
  token?: string;
  expiresAt?: string;
}

/** Request body for POST /auth/complete-invitation. */
export interface CompleteInvitationRequest {
  token: string;
  password: string;
  phoneNumber: string;
  name: string;
  /** Must be true — user accepted Terms of Service and Privacy Policy. */
  agreedToTerms: boolean;
  /** Must be true — explicit opt-in to SMS verification codes. */
  agreedToSmsConsent: boolean;
}

/** Response from POST /auth/complete-invitation. User is pending-verification; frontend continues to verify-email → verify-phone → setup-2FA (same as self-register). */
export interface CompleteInvitationResponse {
  user: User;
  message: string;
  emailCode?: string;
  phoneCode?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  twoFactorCode?: string;
  /**
   * Keep me signed in. Default false.
   * True → 30-day refresh cookie. False → session cookie (cleared when the browser closes).
   */
  persistSession?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  /** Refresh token is delivered via HttpOnly cookie; omitted from response body. */
  refreshToken?: string;
  expiresIn: number;
  user: User;
  requires2FA?: boolean;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface VerifyPhoneRequest {
  userId?: string;
  email?: string;
  code: string;
}

export interface ResendEmailCodeRequest {
  email: string;
}

export interface ResendPhoneCodeRequest {
  email?: string;
  userId?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

/** Response when email/phone verification is required (no tokens; redirect to verify-email or verify-phone) */
export interface LoginRequiresVerificationResponse {
  requiresVerification: true;
  /** Email from login form (password already verified). */
  email: string;
  userId: string;
  name: string;
  role: User['role'];
  phoneNumber: string;
  emailVerified: boolean;
  phoneVerified: boolean;
}

/** Response when 2FA is required at login (no tokens yet) */
export interface LoginRequires2FAResponse {
  requires2FA: true;
  tempToken: string;
  expiresIn: number;
  /** 'totp' = authenticator app; 'sms' = code sent to phone */
  twoFactorMethod?: 'totp' | 'sms';
  /**
   * True for parent and tutor so the 2FA screen can show Remember this device.
   * Never inferred from email before the password is verified.
   */
  canRememberDevice?: boolean;
  user: Pick<User, 'userId' | 'email' | 'name'>;
}

/** Request body for POST /auth/enable-2fa. Default is 'totp'. */
export interface Enable2FARequest {
  method?: 'totp' | 'sms';
  /**
   * `onboarding` — signup 2FA step. SMS may enable immediately when phoneVerified.
   * `account` (default) — always challenge SMS with a new code.
   */
  context?: 'onboarding' | 'account';
}

export interface Enable2FAResponse {
  /** TOTP path: secret and QR for authenticator app */
  secret?: string;
  otpauthUrl?: string;
  qrCodeDataUrl?: string;
  /** SMS path: method and message; in local dev smsCode may be returned */
  method?: 'totp' | 'sms';
  message?: string;
  smsCode?: string;
  /** SMS onboarding: true when 2FA was activated without a second text. */
  enabled?: boolean;
}

export interface Verify2FASetupRequest {
  code: string;
}

export interface Verify2FASetupResponse {
  backupCodes: string[];
  message: string;
}

export interface Verify2FALoginRequest {
  tempToken: string;
  code: string;
  /** Remember this device (parent/tutor only). Ignored for leadership roles. */
  rememberDevice?: boolean;
}

/** Request for POST /auth/send-2fa-code (SMS 2FA users at login) */
export interface Send2FACodeRequest {
  tempToken: string;
}

/** Response: in local dev smsCode may be included */
export interface Send2FACodeResponse {
  message: string;
  smsCode?: string;
}

// ============================================================================
// USERS / ADMIN (Phase 1 Week 3)
// ============================================================================

/** Response from GET /users (list users) */
export interface ListUsersResponse {
  users: User[];
  count: number;
  /** 1-based page when using offset pagination. */
  page?: number;
  /** Total pages for the filtered/sorted set. */
  totalPages?: number;
  /** Total users matching filters (before paging). */
  totalCount?: number;
  /** Page size applied by the server. */
  limit?: number;
  /**
   * Present when includePendingQueue=1.
   * Up to 50 pending-approval users for the Registrations to review card.
   */
  pendingApprovalUsers?: User[];
  /** @deprecated Cursor pagination removed; sort requires offset pages. */
  nextToken?: string;
}

/** Minimal user fields for admin lists (class tutor column, semester tutors). */
export interface UserDisplaySummary {
  userId: string;
  name: string;
  email?: string;
  role?: string;
  profileImageUrl?: string | null;
}

/** Response from GET /users/summaries?ids= */
export interface ListUserSummariesResponse {
  users: UserDisplaySummary[];
}

/** Request for POST /users/approve */
export interface ApproveUserRequest {
  userId: string;
}

/** Response from POST /users/approve */
export interface ApproveUserResponse {
  user: User;
  message: string;
}

/** Request for POST /users/reject */
export interface RejectUserRequest {
  userId: string;
  reason?: string;
}

/** Response from POST /users/reject */
export interface RejectUserResponse {
  message: string;
  userId: string;
}

/** Request body for PUT /users/:userId (admin edit profile / change role) */
export interface UpdateUserRequest {
  name?: string;
  email?: string;
  phoneNumber?: string;
  /** Built-in role key or custom role id. */
  role?: string;
  /** When true, user is not required to complete family registration and is excluded from registration counts. */
  familyRegistrationExempt?: boolean;
  /** Mark/unmark as a returning family (grants returning-family early enrollment access). */
  returningFamily?: boolean;
  /** Account home address. Does not update per-year family registration records. */
  homeAddress?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  preferredClassPaymentMethod?: ClassPaymentMethod;
  classPaymentHandle?: string | null;
  tutorBhgRoleAtBhg?: 'director' | 'board-member' | 'other' | null;
  tutorBhgRoleAtBhgOther?: string | null;
  tutorEducation?: string | null;
  tutorExperience?: string | null;
  tutorBiography?: string | null;
}

/** Response from PUT /users/:userId */
export interface UpdateUserResponse {
  user: User;
  message: string;
  /** True when a new email was staged and confirmation was sent (login email unchanged until confirm). */
  emailChangePending?: boolean;
}

/** Request body for POST /users/returning-families/import — flags existing users by email. */
export interface ImportReturningFamiliesRequest {
  /** Email addresses of returning families (matched case-insensitively to existing accounts). */
  emails: string[];
}

export interface ImportReturningFamiliesMatch {
  userId: string;
  email: string;
  name: string;
}

/** Result of a returning-families import. Additive: only flags accounts not already flagged. */
export interface ImportReturningFamiliesResponse {
  /** Accounts newly flagged as returning by this import. */
  flagged: ImportReturningFamiliesMatch[];
  /** Accounts that matched but were already flagged (left unchanged). */
  alreadyFlagged: ImportReturningFamiliesMatch[];
  /** Emails with no matching account (nothing created — only current users are flagged). */
  notFound: string[];
  message: string;
}

/** Request body for POST /users/returning-families/clear. */
export interface ClearReturningFamiliesRequest {
  /** Must be exactly "CLEAR RETURNING FLAGS". */
  confirmationText: string;
}

/** Result of clearing all returningFamily flags. */
export interface ClearReturningFamiliesResponse {
  clearedCount: number;
  message: string;
}

/** Response from POST /users/:userId/send-password-reset */
export interface SendPasswordResetResponse {
  message: string;
}

/** Response from GET /users/:userId */
export interface GetUserResponse {
  user: User;
  /** Permission keys for this user's role (built-in or custom). Read-only for Phase B. */
  effectivePermissions?: string[];
}

// ============================================================================
// ROLES (custom roles – Phase 1 permission UI)
// ============================================================================

/** Role returned by API. Built-in roles have isBuiltIn: true and no roleId; custom roles have roleId. */
export interface Role {
  /** Custom role id (e.g. "role_<uuid>"); omitted for built-in roles. */
  roleId?: string;
  /** Display name (e.g. "Admin", "Regional Coordinator"). */
  name: string;
  /** For API: role key used in user.role (built-in key or roleId for custom). */
  value: string;
  /** True for super-admin, admin, board-member, tutor, parent. */
  isBuiltIn: boolean;
  /** Permission keys for this role. Only present when requested (get role, list with permissions). */
  permissionKeys?: string[];
  /** Code defaults for built-in roles (GET role). */
  defaultPermissionKeys?: string[];
  /** Whether manage-roles user can edit permissions on the role edit page. */
  canEditPermissions?: boolean;
  /** Whether "Reset to defaults" is available (built-in override exists). */
  canResetToDefaults?: boolean;
  /** True when built-in role permissions differ from code defaults. */
  permissionsModified?: boolean;
}

/** Response from GET /roles */
export interface ListRolesResponse {
  roles: Role[];
}

/** Response from GET /roles/:roleId */
export interface GetRoleResponse {
  role: Role;
}

/** Request body for POST /roles (create custom role) */
export interface CreateRoleRequest {
  name: string;
  permissionKeys: string[];
}

/** Request body for PUT /roles/:roleId */
export interface UpdateRoleRequest {
  name: string;
  permissionKeys: string[];
}

/** Request for POST /users/deactivate, /users/activate, /users/delete */
export interface UserIdRequest {
  userId: string;
}

/** Response from POST /users/deactivate, POST /users/activate */
export interface UserActionResponse {
  user: User;
  message: string;
}

/** Response from POST /users/delete */
export interface DeleteUserResponse {
  message: string;
  userId: string;
}

/** Single audit log entry (from GET /audit/logs) */
export interface AuditLogEntry {
  auditId: string;
  timestamp: number;
  actorUserId: string;
  action: string;
  targetType?: string;
  targetId?: string;
  details?: Record<string, unknown>;
  /** Resolved by API for display: actor name */
  actorName?: string;
  /** Resolved by API for display: actor email */
  actorEmail?: string;
  /** Resolved by API for display: e.g. "Spring 2026", "Jane Doe (jane@example.com)", "Class: Math 101" */
  targetDisplay?: string;
  /** When target is a user, true so UI can link to profile */
  targetIsUser?: boolean;
  /** Stored CSV export; UI can fetch presigned download via exportId (targetId) */
  targetIsDataExport?: boolean;
}

/** Response from GET /audit/logs */
export interface ListAuditLogsResponse {
  entries: AuditLogEntry[];
  nextToken?: string;
  count: number;
  /** Total matching entries (when not using search). Enables "Page X of Y". */
  totalCount?: number;
  /** Total pages (when not using search). */
  totalPages?: number;
  /** 1-based page number for this response. */
  currentPage?: number;
}

// ============================================================================
// SEMESTER TYPES (Phase 2 Week 4)
// ============================================================================

export type SemesterStatus = 'planning' | 'active' | 'inactive' | 'archived';

/** Who may enroll when a semester enrollment window opens. */
export type SemesterEnrollmentAudienceType =
  | 'roles'
  | 'returning_families'
  | 'new_families'
  | 'all_parents';

/** Advance reminder before class submission deadline (Eastern wall clock on parent deadline). */
export interface SemesterSubmissionDeadlineReminder {
  reminderId: string;
  /** Optional label for directors, e.g. "One week before". */
  label?: string;
  /** Days before deadlineAt to notify invited tutors (0 = no advance reminder). */
  remindDaysBefore: number;
}

/** Tutor class proposal submission deadline for a semester. */
export interface SemesterClassSubmissionDeadline {
  /** When proposals must be submitted (UTC ms; UI uses America/New_York). */
  deadlineAt: number;
  /** When true, tutors cannot submit or resubmit for review after deadlineAt. */
  blockLateSubmitForReview: boolean;
  reminders: SemesterSubmissionDeadlineReminder[];
}

export interface SemesterEnrollmentWindow {
  windowId: string;
  /** Short label for directors, e.g. "Tutor priority week". */
  label: string;
  /** When this window opens (ms since epoch). */
  opensAt: number;
  audienceType: SemesterEnrollmentAudienceType;
  /** When audienceType is `roles`, which roles may enroll. */
  roles?: string[];
  /**
   * For returning_families / new_families: prior semester to check for family enrollment history.
   * Optional for returning_families (flagged users still match without it — first-semester bootstrap).
   * Optional for new_families only when no prior semester exists (then "new" = not flagged).
   */
  referenceSemesterId?: string;
  /**
   * Days before opensAt to notify eligible users (0 = no advance reminder; open notification still sent).
   */
  remindDaysBefore?: number;
}

export interface Semester {
  semesterId: string;
  name: string;
  status: SemesterStatus;
  startDate: number;
  endDate: number;
  classesVisibleDate?: number;
  boardSignupDate?: number;
  publicSignupDate?: number;
  signupEndDate?: number;
  /** Flexible enrollment open dates by audience (preferred over legacy signup dates when set). */
  enrollmentWindows?: SemesterEnrollmentWindow[];
  /** Tutor class submission deadline and advance reminders for this semester. */
  classSubmissionDeadline?: SemesterClassSubmissionDeadline;
  /** Ordered list of schedule column definitions (label + gradeCodes). Omit/empty = use DEFAULT_SCHEDULE_COLUMNS. Legacy: may be string[] (keys); normalize when reading. */
  scheduleColumns?: ScheduleColumnDefinition[];
  /** Optional Thursday for missed regular Thursdays (ms at start of calendar day). Shown on schedule export. */
  makeupDate?: number;
  /** Optional footer for schedule PDF (extra rooms, playground, shared spaces). */
  scheduleExtraRoomsNotes?: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  activatedAt?: number;
  activatedBy?: string;
  archivedAt?: number;
  archivedBy?: string;
}

export interface SemesterTeacher {
  semesterTeacherId: string;
  semesterId: string;
  teacherId: string;
  invitedBy: string;
  invitedAt: number;
  acceptedAt?: number;
  createdAt: number;
}

/** Request for POST /semesters */
export interface CreateSemesterRequest {
  name: string;
  startDate: number;
  endDate: number;
  classesVisibleDate?: number;
  boardSignupDate?: number;
  publicSignupDate?: number;
  signupEndDate?: number;
  enrollmentWindows?: SemesterEnrollmentWindow[];
  classSubmissionDeadline?: SemesterClassSubmissionDeadline;
}

/** GET /semesters/:semesterId/enrollment-access */
export interface SemesterEnrollmentAccessResponse {
  canEnroll: boolean;
  message?: string;
  nextOpensAt?: number;
}

/** Request for PUT /semesters/:semesterId */
export interface UpdateSemesterRequest {
  name?: string;
  startDate?: number;
  endDate?: number;
  classesVisibleDate?: number;
  boardSignupDate?: number;
  publicSignupDate?: number;
  /** Pass null to clear the signup close date; omit to leave unchanged. */
  signupEndDate?: number | null;
  enrollmentWindows?: SemesterEnrollmentWindow[];
  /** Pass null to clear; omit to leave unchanged. */
  classSubmissionDeadline?: SemesterClassSubmissionDeadline | null;
  /** Ordered list of schedule column definitions (label + gradeCodes). Pass [] to reset to default. */
  scheduleColumns?: ScheduleColumnDefinition[];
  /** Optional Thursday for missed regular Thursdays (ms); pass null to clear. */
  makeupDate?: number | null;
  /** Extra rooms/spaces blurb for schedule PDF; pass null or '' to clear. */
  scheduleExtraRoomsNotes?: string | null;
}

/** Per-semester class status buckets for Semesters list chips (GET /semesters?includeClassSummary=1). */
export type SemesterClassCountsByStatus = {
  approved: number;
  submitted: number;
  underReview: number;
  rejected: number;
  draft: number;
  withdrawn: number;
};

/** Response from GET /semesters (list) */
export interface ListSemestersResponse {
  semesters: Semester[];
  nextToken?: string;
  count: number;
  /**
   * Present when includeClassSummary=1 and the caller has list-classes.
   * Status buckets for non-deleted classes in each returned semester.
   */
  classCountsBySemester?: Record<string, SemesterClassCountsByStatus>;
  /**
   * Present when includeClassSummary=1 and the caller has list-classes.
   * Unique teacherIds with at least one non-deleted class in each semester.
   */
  teacherCountsBySemester?: Record<string, number>;
}

/** Request for POST /semesters/:semesterId/activate. confirmationText must be "ACTIVATE SEMESTER". */
export interface ActivateSemesterRequest {
  confirmationText: string;
}

/** Response from POST /semesters/:semesterId/activate. Lists semesters that were set to inactive. */
export interface ActivateSemesterResponse {
  semester: Semester;
  message: string;
  deactivatedSemesters?: Array<{ semesterId: string; name: string }>;
}

/** Request for POST /semesters/:semesterId/archive */
export interface ArchiveSemesterRequest {
  /** Optional confirmation for future use */
  confirm?: boolean;
}

/** Request for POST /semesters/:semesterId/invite-tutor */
export interface InviteTutorRequest {
  tutorId: string;
}

/** Response from POST /semesters/:semesterId/invite-tutor */
export interface InviteTutorResponse {
  message: string;
  semesterTeacherId: string;
}

/** Response from GET /semesters/:semesterId/tutors */
export interface ListSemesterTutorsResponse {
  tutors: Array<{
    semesterTeacherId: string;
    tutorId: string;
    invitedBy: string;
    invitedAt: number;
    acceptedAt?: number;
    /** Populated from Users table when available */
    tutor?: Pick<User, 'userId' | 'email' | 'name' | 'role'>;
  }>;
  count: number;
}

// ============================================================================
// TIME SLOTS (for class preferred time; schedule builder uses these / settings later)
// ============================================================================

/** Single time slot: 9am–10am through 12:30–1:30, excluding 12–12:30 (lunch). */
export interface TimeSlot {
  id: string;
  /** Display label e.g. "9:00 AM – 10:00 AM" */
  label: string;
  /** Start time "HH:mm" 24h for sorting/API */
  startTime: string;
  /** End time "HH:mm" 24h */
  endTime: string;
}

/**
 * Default time slots for the semester. Used when creating/editing a class (optional preferred slot)
 * and by the schedule builder. Later: replace with settings/DB when "settings for the web app" exist.
 */
export const TIME_SLOTS: TimeSlot[] = [
  { id: '09:00-10:00', label: '9:00 AM – 10:00 AM', startTime: '09:00', endTime: '10:00' },
  { id: '10:00-11:00', label: '10:00 AM – 11:00 AM', startTime: '10:00', endTime: '11:00' },
  { id: '11:00-12:00', label: '11:00 AM – 12:00 PM', startTime: '11:00', endTime: '12:00' },
  { id: '12:30-13:30', label: '12:30 PM – 1:30 PM', startTime: '12:30', endTime: '13:30' },
];

export const TIME_SLOT_IDS = TIME_SLOTS.map((s) => s.id);

// ============================================================================
// CLASS TYPES (Phase 2 Week 5)
// ============================================================================

export type ClassStatus =
  | 'draft'
  | 'submitted'
  | 'under-review'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

/** Allowed grade level values (one or more per class for crossover) */
export const GRADE_LEVELS = [
  'Preschool',
  'Elementary',
  'Middle School',
  'High School',
  'Adult',
] as const;
export type GradeLevel = (typeof GRADE_LEVELS)[number];

/** Sub-grade options per band for class form (when teacher narrows to specific grades). null = no sub-grades (Preschool, Adult). */
export const SUB_GRADE_OPTIONS: Record<GradeLevel, string[] | null> = {
  Preschool: null,
  Elementary: ['K', '1', '2', '3', '4', '5'],
  'Middle School': ['6', '7', '8'],
  'High School': ['9', '10', '11', '12'],
  Adult: null,
};

// ---------------------------------------------------------------------------
// SCHEDULE COLUMNS (flexible grade columns per semester)
// ---------------------------------------------------------------------------

/** Allowed grade codes for building columns: preschool, K, 1–12, adult. Directors can pair any (e.g. 1-2, 1-4, 6-8). */
export const ALL_GRADE_CODES = [
  'preschool',
  'K',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  'adult',
] as const;
export type GradeCode = (typeof ALL_GRADE_CODES)[number];

/** One schedule column: custom label and the grade codes it includes (e.g. ["1","2"] for 1st–2nd, ["6","7","8"] for middle). */
export interface ScheduleColumnDefinition {
  /** Display label (e.g. "K", "1st–2nd", "1st–4th", "Middle") */
  label: string;
  /** Grade codes in this column: e.g. ["preschool"], ["K"], ["1","2"], ["6","7","8"] */
  gradeCodes: string[];
}

/** Which grade band a grade code belongs to (for matching classes). */
export function getBandForGradeCode(code: string): GradeLevel | null {
  const c = code.trim();
  const lower = c.toLowerCase();
  if (lower === 'preschool') return 'Preschool';
  if (lower === 'adult') return 'Adult';
  if (c === 'K' || c === 'k' || ['1', '2', '3', '4', '5'].includes(c)) return 'Elementary';
  if (['6', '7', '8'].includes(code)) return 'Middle School';
  if (['9', '10', '11', '12'].includes(code)) return 'High School';
  return null;
}

/** Build a display label from grade codes (e.g. ["1","2"] → "1st–2nd", ["6","7","8"] → "6th–8th"). */
export function gradeCodesToLabel(gradeCodes: string[]): string {
  if (gradeCodes.length === 0) return '';
  if (gradeCodes.includes('preschool')) return 'Preschool';
  if (gradeCodes.includes('adult')) return 'Adult';
  const numeric = gradeCodes.filter((c) => c !== 'K' && c !== 'preschool' && c !== 'adult').map(Number).sort((a, b) => a - b);
  const hasK = gradeCodes.includes('K');
  if (hasK && numeric.length === 0) return 'K';
  if (hasK && numeric.length > 0) {
    const numPart = formatGradeRange(numeric);
    return numPart ? `K, ${numPart}` : 'K';
  }
  return formatGradeRange(numeric);
}

function formatGradeRange(sorted: number[]): string {
  if (sorted.length === 0) return '';
  if (sorted.length === 1) return ordinal(sorted[0]);
  const low = sorted[0];
  const high = sorted[sorted.length - 1];
  return `${ordinal(low)}–${ordinal(high)}`;
}

function ordinal(n: number): string {
  const s = String(n);
  if (n >= 11 && n <= 13) return `${s  }th`;
  switch (n % 10) {
    case 1: return `${s  }st`;
    case 2: return `${s  }nd`;
    case 3: return `${s  }rd`;
    default: return `${s  }th`;
  }
}

/** Default columns when semester has no scheduleColumns: one per grade band. */
export const DEFAULT_SCHEDULE_COLUMNS: ScheduleColumnDefinition[] = [
  { label: 'Preschool', gradeCodes: ['preschool'] },
  { label: 'Elementary', gradeCodes: ['K', '1', '2', '3', '4', '5'] },
  { label: 'Middle School', gradeCodes: ['6', '7', '8'] },
  { label: 'High School', gradeCodes: ['9', '10', '11', '12'] },
  { label: 'Adult', gradeCodes: ['adult'] },
];

/** Legacy: old semester data may have scheduleColumns as string keys. Normalize to ScheduleColumnDefinition[]. */
export const LEGACY_SCHEDULE_KEYS_TO_DEF: Record<string, ScheduleColumnDefinition> = {
  preschool: { label: 'Preschool', gradeCodes: ['preschool'] },
  k: { label: 'K', gradeCodes: ['K'] },
  '1-2': { label: '1st–2nd', gradeCodes: ['1', '2'] },
  '3': { label: '3rd', gradeCodes: ['3'] },
  '4-5': { label: '4th–5th', gradeCodes: ['4', '5'] },
  elementary: { label: 'Elementary', gradeCodes: ['K', '1', '2', '3', '4', '5'] },
  middle: { label: 'Middle', gradeCodes: ['6', '7', '8'] },
  '6': { label: '6th', gradeCodes: ['6'] },
  '7': { label: '7th', gradeCodes: ['7'] },
  '8': { label: '8th', gradeCodes: ['8'] },
  high: { label: 'High', gradeCodes: ['9', '10', '11', '12'] },
  '9': { label: '9th', gradeCodes: ['9'] },
  '10': { label: '10th', gradeCodes: ['10'] },
  '11': { label: '11th', gradeCodes: ['11'] },
  '12': { label: '12th', gradeCodes: ['12'] },
  adult: { label: 'Adult', gradeCodes: ['adult'] },
};

/** Normalize scheduleColumns from API (may be legacy string[] or ScheduleColumnDefinition[]) to ScheduleColumnDefinition[]. */
export function normalizeScheduleColumns(
  raw: unknown
): ScheduleColumnDefinition[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_SCHEDULE_COLUMNS;
  const first: unknown = raw[0];
  if (typeof first === 'string') {
    return (raw as string[]).map((key) => LEGACY_SCHEDULE_KEYS_TO_DEF[key] ?? { label: key, gradeCodes: [key] });
  }
  if (typeof first === 'object' && first !== null && 'label' in first && 'gradeCodes' in first) {
    return raw as ScheduleColumnDefinition[];
  }
  return DEFAULT_SCHEDULE_COLUMNS;
}

export const CLASS_EXCERPT_MAX_LENGTH = 200;

/** A single review comment on a class (board or teacher). */
export interface ClassReviewComment {
  commentId: string;
  userId: string;
  body: string;
  createdAt: number;
}

export interface Class {
  classId: string;
  semesterId: string;
  teacherId: string;
  title: string;
  description: string;
  /** Single level for backward compat; prefer gradeLevels when present */
  gradeLevel?: string;
  /** One or more grade levels (e.g. Elementary + Middle School crossover) */
  gradeLevels?: GradeLevel[];
  /** Optional sub-grades within bands: e.g. ["1","2"] for 1st–2nd in Elementary, ["6","7","8"] for Middle. Empty/omit = all grades in selected band(s). */
  gradeSubLevels?: string[];
  ageRange?: string;
  /** Min age in years (for sign-up eligibility checks) */
  minAge?: number;
  /** Max age in years (for sign-up eligibility checks) */
  maxAge?: number;
  maxStudents: number;
  /**
   * Atomic active (non-waitlisted, non-deleted) enrollment count.
   * Seeded on first enroll/catalog touch; reserved/released via TransactWrite.
   */
  activeEnrollmentCount?: number;
  /** Short write-up for landing page cards / SEO (max CLASS_EXCERPT_MAX_LENGTH) */
  excerpt?: string;
  /** Price in dollars (e.g. 25.00); optional */
  priceTotal?: number;
  /**
   * When true and the class is at maxStudents, families can join a waitlist instead of being blocked.
   * Tutor can toggle this on/off at any time via PATCH /classes/:classId/waitlist.
   */
  waitlistEnabled?: boolean;
  /** Whether teacher allows a family discount for multiple children in same class */
  allowsFamilyDiscount?: boolean;
  /** Optional dollar amount for family discount (e.g. 10 or 20). Shown to parents when allowsFamilyDiscount is true. */
  familyDiscountAmount?: number;
  /** Class cover image: URL from Unsplash (or other CDN) */
  imageUrl?: string;
  /** Class cover image: our resource ID when teacher uploaded an image */
  imageResourceId?: string;
  /** Attribution for image (e.g. "Photo by Name on Unsplash"). Use with imagePhotographerUrl for linked attribution (Unsplash guidelines). */
  imageAttribution?: string;
  /** Photographer profile URL (e.g. https://unsplash.com/@username) for attribution link. */
  imagePhotographerUrl?: string;
  status: ClassStatus;
  rejectionReason?: string;
  withdrawalReason?: string;
  /** Teacher's optional preferred time slot when creating/editing class (id from TIME_SLOTS). Legacy; prefer preferredScheduleStartTime/End. */
  preferredTimeSlotId?: string;
  /** Tutor preferred meeting time (HH:mm, 24h). */
  preferredScheduleStartTime?: string;
  preferredScheduleEndTime?: string;
  /** Assigned by schedule builder (TIME_SLOTS id or `lunch`). */
  timeSlotId?: string;
  /** Actual class meeting time on Thursdays (HH:mm). Shown on schedule when set. */
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  /** Classroom / room number (admin). Included on schedule PDF. */
  roomNumber?: string;
  /** How the current timeSlotId was set: 'auto' (Auto schedule) or 'manual' (user drag). Persisted so refresh keeps green vs orange. */
  scheduleAssignmentSource?: 'auto' | 'manual';
  createdAt: number;
  updatedAt: number;
  submittedAt?: number;
  approvedAt?: number;
  approvedBy?: string;
  rejectedAt?: number;
  rejectedBy?: string;
  withdrawnAt?: number;
  /** Optional comment when board requested changes (status → under-review). */
  requestChangesComment?: string;
  requestChangesAt?: number;
  requestChangesBy?: string;
  /** Review comments (board/teacher). */
  reviewComments?: ClassReviewComment[];
  /** Comment likes: commentId -> list of userIds who liked. */
  commentLikes?: Record<string, string[]>;
  /** Emoji reactions per comment: commentId -> list of { emoji, userIds }. */
  commentReactions?: Record<string, Array<{ emoji: string; userIds: string[] }>>;
  isDeleted: boolean;
  deletedAt?: number;
}

export interface ClassResource {
  resourceId: string;
  classId: string;
  name: string;
  fileUrl: string;
  fileKey: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: number;
  isDeleted: boolean;
  deletedAt?: number;
}

/** Request for POST /semesters/:semesterId/classes (create draft) */
export interface CreateClassRequest {
  title: string;
  description: string;
  /** At least one required */
  gradeLevels: GradeLevel[];
  /** Optional sub-grades within bands (e.g. ["1","2"] for 1st–2nd in Elementary). */
  gradeSubLevels?: string[];
  ageRange?: string;
  minAge?: number;
  maxAge?: number;
  maxStudents: number;
  excerpt?: string;
  priceTotal: number;
  allowsFamilyDiscount?: boolean;
  /** Optional dollar amount for family discount (e.g. 10 or 20). */
  familyDiscountAmount?: number;
  imageUrl?: string;
  imageResourceId?: string;
  imageAttribution?: string;
  imagePhotographerUrl?: string;
  /** Optional preferred time slot id (from TIME_SLOTS). Legacy. */
  preferredTimeSlotId?: string;
  preferredScheduleStartTime?: string;
  preferredScheduleEndTime?: string;
  waitlistEnabled?: boolean;
}

/** Request for PUT /classes/:classId */
export interface UpdateClassRequest {
  title?: string;
  description?: string;
  gradeLevels?: GradeLevel[];
  /** Optional sub-grades within bands; send null or [] to mean "all grades" for selected band(s). */
  gradeSubLevels?: string[] | null;
  ageRange?: string;
  /** Omit or number to set; send null to clear. */
  minAge?: number | null;
  /** Omit or number to set; send null to clear. */
  maxAge?: number | null;
  maxStudents?: number;
  excerpt?: string;
  priceTotal?: number;
  allowsFamilyDiscount?: boolean;
  /** Optional dollar amount; send null to clear. */
  familyDiscountAmount?: number | null;
  imageUrl?: string | null;
  imageResourceId?: string | null;
  imageAttribution?: string | null;
  imagePhotographerUrl?: string | null;
  /** Optional preferred time slot id; send null to clear. */
  preferredTimeSlotId?: string | null;
  preferredScheduleStartTime?: string | null;
  preferredScheduleEndTime?: string | null;
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  roomNumber?: string | null;
  waitlistEnabled?: boolean;
}

/** Request for POST /classes/:classId/withdraw */
export interface WithdrawClassRequest {
  withdrawalReason: string;
}

/** Response from GET /classes (list) */
export interface ListClassesResponse {
  classes: Class[];
  nextToken?: string;
  count: number;
}

// ============================================================================
// FAMILY REGISTRATION & CHILDREN (Phase 3 Week 8)
// ============================================================================

/** Child record (CHILDREN table): see `Child` interface above. gradeLevel: preschool, K, 1..12, adult. */

/** Payment method when marking a family registration as paid. */
export type FamilyRegistrationPaymentMethod = 'cash' | 'check';

/** One family registration per user per school year (FAMILY_REGISTRATIONS table). */
export interface FamilyRegistration {
  userId: string;
  schoolYear: string;
  familyName: string;
  fatherName: string;
  motherName: string;
  phone: string;
  email: string;
  homeAddress: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  churchValue: string;
  churchOther: string;
  familyNotes: string;
  medicalSignatureDataUrl: string | null;
  liabilitySignatureDataUrl: string | null;
  agreedPolicies: boolean;
  agreedDisciplinary: boolean;
  /** Document IDs the family agreed to (dynamic org documents). */
  agreedDocumentIds?: string[];
  /** Snapshot of agreed document titles at registration time (for PDF/audit). */
  agreedDocuments?: { documentId: string; title: string }[];
  completedAt: number;
  createdAt: number;
  updatedAt: number;
  /** When the registration was marked paid (admin). */
  paidAt?: number;
  /** How payment was received. */
  paymentMethod?: FamilyRegistrationPaymentMethod;
  /** Check number when paymentMethod is 'check'. */
  checkNumber?: string;
  /** User ID of the admin who recorded the payment (audit). */
  paidByUserId?: string;
  /**
   * Yearly-fee discount (dollar amount off the site fee). Still unpaid until marked paid.
   * Collected totals use the remaining amount due (fee minus this discount).
   */
  feeDiscountApplied?: boolean;
  /** Amount taken off the yearly fee, in cents. */
  feeDiscountCents?: number;
  feeDiscountAppliedAt?: number;
  feeDiscountAppliedByUserId?: string;
}

/** Child as sent in POST body (id may be childId from DB or client temp id). */
export interface PostFamilyRegistrationChild {
  id: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  grade: string;
}

/** Request for POST /family-registration */
export interface PostFamilyRegistrationRequest {
  familyName: string;
  fatherName: string;
  motherName: string;
  phone: string;
  email: string;
  homeAddress: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  churchValue: string;
  churchOther: string;
  familyNotes: string;
  children: PostFamilyRegistrationChild[];
  medicalSignatureDataUrl: string | null;
  liabilitySignatureDataUrl: string | null;
  agreedPolicies: boolean;
  agreedDisciplinary: boolean;
  /** IDs from organization documents with requiresAgreementOnRegistration. */
  agreedDocumentIds?: string[];
}

/** Response from GET /family-registration/status */
export interface GetFamilyRegistrationStatusResponse {
  isComplete: boolean;
  schoolYear: string;
}

/** Response from GET /family-registration/churches – distinct "other" church names from past registrations. */
export interface GetFamilyRegistrationChurchesResponse {
  churches: string[];
}

/** Response from GET /family-registration (pre-fill data) */
export interface GetFamilyRegistrationResponse {
  registration: FamilyRegistration | null;
  children: Child[];
  currentSchoolYear: string;
}

/** One row for admin list of family registrations (by school year). */
export interface AdminFamilyRegistrationSummary {
  userId: string;
  schoolYear: string;
  /** Family name from registration form. */
  familyName: string;
  /** Account holder full name (user's name). */
  parentName: string;
  childrenNames: string[];
  emergencyContactName: string;
  emergencyContactPhone: string;
  completedAt: number;
  paidAt?: number;
  paymentMethod?: FamilyRegistrationPaymentMethod;
  checkNumber?: string;
  paidByUserId?: string;
  /** Yearly-fee discount applied (still unpaid until marked paid). */
  feeDiscountApplied?: boolean;
  /** Amount taken off the yearly fee, in cents. */
  feeDiscountCents?: number;
  /**
   * True when this family has ≥1 non-deleted enrollment in an approved class
   * in the active semester (dashboard “Families enrolled” rule).
   */
  hasEnrollment: boolean;
}

/** Response from GET /admin/family-registrations?schoolYear= */
export interface ListFamilyRegistrationsResponse {
  schoolYear: string;
  registrations: AdminFamilyRegistrationSummary[];
  /** Present when the request was paginated (limit param). 1-based. */
  page?: number;
  /** Total pages for the filtered set (paginated requests only). */
  totalPages?: number;
  /** Total registrations matching the filters (paginated requests only). */
  totalCount?: number;
  /** Page size applied by the server (paginated requests only). */
  limit?: number;
}

// ============================================================================
// ENROLLMENTS (Phase 4)
// ============================================================================

export type PaymentStatus =
  | 'unpaid'
  | 'partial'
  | 'paid-cash'
  | 'paid-venmo'
  | 'paid-paypal';

export interface Enrollment {
  enrollmentId: string;
  classId: string;
  childId: string;
  parentUserId: string;
  semesterId: string;
  paymentStatus: PaymentStatus;
  paymentAmount?: number;
  paymentDate?: number;
  paymentNotes?: string;
  /** Tutor marked family discount on this enrollment (reduces fee when class allows it). */
  familyDiscountApplied?: boolean;
  /**
   * Tutor-applied tuition discount in dollars for this student (e.g. financial assistance).
   * Subtracted from the class fee after any family discount. Omit or 0 = none.
   */
  tuitionDiscountAmount?: number;
  enrolledAt: number;
  enrolledBy: string;
  updatedAt: number;
  isDeleted?: boolean;
  deletedAt?: number;
  /** Tutor/admin enrolled despite grade/age mismatch (co-op exception). */
  eligibilityOverride?: boolean;
  /** Why the exception was allowed (admin note). */
  eligibilityOverrideNote?: string;
  /** True when the class tutor manually enrolled this student (not parent self-enroll). */
  manuallyAddedByTutor?: boolean;
  /**
   * When true, this enrollment is on the waitlist (class was full at enrollment time).
   * Tutor can activate the enrollment (move to active roster) via PATCH /enrollments/:id/activate.
   */
  waitlisted?: boolean;
  /**
   * Set only on GET /enrollments?classId= (roster). Not stored in Dynamo.
   * Child’s display name for tutors/admins.
   */
  studentName?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). Not stored in Dynamo.
   * Parent’s display name for tutors/admins.
   */
  parentName?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). Child gradeLevel for display/export.
   */
  studentGrade?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). From child record; not stored on Enrollment item.
   */
  studentAllergies?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). From child record; not stored on Enrollment item.
   */
  studentSpecialComments?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). From family registration for the class school year.
   */
  emergencyContactName?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). From family registration for the class school year.
   */
  emergencyContactPhone?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). Age in years from child birthDate.
   */
  studentAge?: number;
  /**
   * Set only on GET /enrollments?classId= (roster). Guardian email (family registration or account).
   */
  parentEmail?: string;
  /**
   * Set only on GET /enrollments?classId= (roster). Guardian phone (family registration or account).
   */
  parentPhone?: string;
  /**
   * Payment ledger rows for this enrollment (newest first). Roster GET only; not stored on Enrollment item.
   */
  payments?: EnrollmentPayment[];
  /**
   * Set on GET /enrollments?parentUserId= (family views). Not stored in Dynamo.
   * Class title so clients need not load the full public catalog.
   */
  classTitle?: string;
}

/** One recorded payment (deposit/installment) for an enrollment. */
export type EnrollmentPaymentMethod = 'paid-cash' | 'paid-venmo' | 'paid-paypal';

export interface EnrollmentPayment {
  paymentId: string;
  enrollmentId: string;
  classId: string;
  amount: number;
  method: EnrollmentPaymentMethod;
  notes?: string;
  paidAt: number;
  recordedByUserId: string;
  createdAt: number;
}

/** Request for POST /enrollments */
export interface CreateEnrollmentRequest {
  classId: string;
  childId: string;
  /** Class tutor or admin only — skip grade/age checks when true. */
  eligibilityOverride?: boolean;
  /** Required when override is used and the child is ineligible. */
  eligibilityOverrideNote?: string;
}

/** Tutor roster add-student search result. */
export interface ClassRosterStudentSearchResult {
  childId: string;
  parentUserId: string;
  studentName: string;
  gradeLevel: string;
  parentName: string;
  parentEmail: string;
}

/** Request for POST /enrollments/:enrollmentId/payments (record a new payment) */
export interface RecordEnrollmentPaymentRequest {
  amount: number;
  method: EnrollmentPaymentMethod;
  notes?: string;
}

/** Response for POST /enrollments/:enrollmentId/payments */
export interface RecordEnrollmentPaymentResponse {
  payment: EnrollmentPayment;
  enrollment: Enrollment;
  payments: EnrollmentPayment[];
}

/** Request for PUT /enrollments/:enrollmentId/payment (tutor or admin — adjust total only) */
export interface SetEnrollmentFamilyDiscountRequest {
  applied: boolean;
}

export interface SetEnrollmentFamilyDiscountResponse {
  enrollment: Enrollment;
}

/** Request for PATCH /enrollments/:enrollmentId/tuition-discount */
export interface SetEnrollmentTuitionDiscountRequest {
  /** Dollars off this student’s fee. Use 0 to clear. */
  amount: number;
}

export interface SetEnrollmentTuitionDiscountResponse {
  enrollment: Enrollment;
}

export interface UpdateEnrollmentPaymentRequest {
  /** How this payment was received (used when marking paid in full). */
  paymentStatus: PaymentStatus;
  /** Total dollars received to date (replaces cumulative total; use POST /payments for new rows). */
  paymentAmount?: number;
  /**
   * @deprecated Record payments via POST /payments. Only `false` is supported on PUT.
   */
  addPayment?: boolean;
}

/** One bucket in GET /dashboard/enrollments-by-grade */
export interface EnrollmentByGradeBucket {
  gradeKey: string;
  count: number;
}

/** Response from GET /dashboard/enrollments-by-grade */
export interface EnrollmentByGradeResponse {
  semesterId: string;
  /** Distinct children with ≥1 enrollment in the semester */
  totalStudents: number;
  /** Distinct parent accounts with ≥1 active enrollment in the semester */
  enrolledFamilies: number;
  buckets: EnrollmentByGradeBucket[];
}

// ============================================================================
// ATTENDANCE (Thursday sessions; tutor for own class)
// ============================================================================

export type AttendanceStatus = 'present' | 'absent';

/** One row per enrollment per class per session date (YYYY-MM-DD) */
export interface ClassAttendanceRecord {
  enrollmentId: string;
  sessionDate: string;
  status: AttendanceStatus;
  recordedByUserId: string;
  updatedAt: number;
}

/** Response from GET /classes/:classId/attendance */
export interface ListClassAttendanceResponse {
  classId: string;
  sessionDate: string;
  records: ClassAttendanceRecord[];
}

/** Response from GET /classes/:classId/attendance (no sessionDate) — dates with saved rows */
export interface ListClassAttendanceSessionsResponse {
  classId: string;
  sessionDates: string[];
}

/** Body for PUT /classes/:classId/attendance */
export interface PutClassAttendanceRequest {
  sessionDate: string;
  records: Array<{ enrollmentId: string; status: AttendanceStatus }>;
}

/** Tutor shown on public class list/detail (no sensitive fields). */
export interface PublicClassTeacherSummary {
  userId: string;
  name: string;
  profileImageUrl?: string | null;
}

/** How families can pay a tutor, shown on the public tutor profile. */
export interface PublicTutorClassPayment {
  method: ClassPaymentMethod;
  /** Display label, e.g. "Venmo", "PayPal", "Cash". */
  methodLabel: string;
  /** Display handle, e.g. "@username" (Venmo) or "username" (PayPal). Absent for cash. */
  handleDisplay?: string;
  /** venmo.com / paypal.me profile URL. Absent for cash. */
  link?: string;
}

/** Approved class in the active semester, linkable from the tutor profile. */
export interface PublicTutorClassSummary {
  classId: string;
  title: string;
}

/** GET /public/tutors/:userId — forward-facing tutor profile (no phone or admin fields). */
export interface PublicTutorProfile {
  userId: string;
  name: string;
  email: string;
  /** City only (from account address), for public tutor profile. */
  city?: string;
  profileImageUrl?: string | null;
  education?: string;
  experience?: string;
  /** Role at BHG and teaching summary for the active semester. */
  activitiesAtBhg: string;
  biography?: string;
  /** Approved class titles in the active semester (when applicable). */
  currentClassTitles: string[];
  /** Approved classes (with IDs) in the active semester, for linking to class pages. */
  currentClasses?: PublicTutorClassSummary[];
  /** How to pay this tutor for classes; only present when payment info is complete. */
  classPayment?: PublicTutorClassPayment;
}

/** POST /tutor-contact — parent question before enrollment (auth required). */
export interface TutorContactRequest {
  tutorUserId: string;
  classId: string;
  message: string;
  honeypot?: string;
}

/** GET /public/classes?semesterId= — approved classes with an assigned schedule slot (enroll + landing) */
export interface PublicClassListItem {
  classId: string;
  title: string;
  excerpt?: string;
  description: string;
  gradeLevel?: string;
  gradeLevels?: GradeLevel[];
  gradeSubLevels?: string[];
  ageRange?: string;
  minAge?: number;
  maxAge?: number;
  maxStudents: number;
  /** Number of active (non-waitlisted) enrollments. Omitted when count is unavailable. */
  enrollmentCount?: number;
  /** True when active enrollment count has reached maxStudents. */
  isFull?: boolean;
  /** Whether the tutor has enabled the waitlist for this class. */
  waitlistEnabled?: boolean;
  priceTotal?: number;
  /** When true, tutor may apply a discount for multiple children from the same family in this class. */
  allowsFamilyDiscount?: boolean;
  /** Dollar amount shown when `allowsFamilyDiscount` is true (optional). */
  familyDiscountAmount?: number;
  imageUrl?: string;
  /** Assigned schedule slot id (set by schedule builder). */
  timeSlotId?: string;
  /** Actual Thursday meeting time (HH:mm) when set on the schedule. */
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  teacher: PublicClassTeacherSummary;
}

/** GET /public/classes/:classId — approved class in active semester only */
export interface PublicClassDetail {
  classId: string;
  semesterId: string;
  title: string;
  description: string;
  excerpt?: string;
  gradeLevel?: string;
  gradeLevels?: GradeLevel[];
  gradeSubLevels?: string[];
  ageRange?: string;
  minAge?: number;
  maxAge?: number;
  maxStudents: number;
  /** Number of active (non-waitlisted) enrollments. */
  enrollmentCount?: number;
  /** True when active enrollment count has reached maxStudents. */
  isFull?: boolean;
  /** Whether the tutor has enabled the waitlist for this class. */
  waitlistEnabled?: boolean;
  priceTotal?: number;
  allowsFamilyDiscount?: boolean;
  familyDiscountAmount?: number;
  imageUrl?: string;
  /** Assigned schedule slot id (set by schedule builder). */
  timeSlotId?: string;
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  imageAttribution?: string;
  imagePhotographerUrl?: string;
  teacher: PublicClassTeacherSummary;
}

// ============================================================================
// VALIDATION TYPES
// ============================================================================

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ============================================================================
// REPORTS (Phase 5)
// ============================================================================

export interface ReportCountBucket {
  label: string;
  count: number;
}

export interface ReportAttendanceSessionBucket {
  sessionDate: string;
  present: number;
  absent: number;
}

/** GET /reports/attendance-per-class */
export interface ReportAttendancePerClassResponse {
  classId: string;
  classTitle: string;
  semesterId: string;
  buckets: ReportAttendanceSessionBucket[];
}

/** GET /reports/bhg-full-attendance */
export interface ReportBhgFullAttendanceResponse {
  semesterId: string;
  buckets: ReportAttendanceSessionBucket[];
}

/** One family row for location report drill-down and export. */
export interface ReportFamilyRegistrationRow {
  userId: string;
  familyName: string;
  parentName: string;
  email: string;
  phone: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  /** Display label for home church */
  church: string;
  /** City + state for display/export */
  cityState: string;
}

/** GET /reports/location-by-zip | location-by-city | churches */
export interface ReportLocationBucketsResponse {
  schoolYear: string;
  buckets: ReportCountBucket[];
  totalFamilies: number;
  /** Present when includeFamilies=true — full registration list for filters/export */
  families?: ReportFamilyRegistrationRow[];
}

/** GET /reports/special-notes-families */
export interface ReportSpecialNotesFamilyRow {
  userId: string;
  familyName: string;
  parentName: string;
  notes: string;
}

export interface ReportSpecialNotesFamiliesResponse {
  schoolYear: string;
  families: ReportSpecialNotesFamilyRow[];
}

/** One child with allergies from the CHILDREN table (parent account). */
export interface ReportChildAllergyRow {
  childId: string;
  parentUserId: string;
  studentName: string;
  gradeLevel: string;
  allergies: string;
  /** Optional tutor notes from the child profile */
  specialComments?: string;
  parentName: string;
  parentEmail: string;
}

/** GET /reports/children-allergies */
export interface ReportChildrenAllergiesResponse {
  totalChildren: number;
  children: ReportChildAllergyRow[];
}

/** One enrolled student row for GET /reports/emergency-contacts */
export interface ReportEmergencyContactRow {
  childId: string;
  parentUserId: string;
  studentName: string;
  gradeLevel: string;
  classTitles: string[];
  familyName?: string;
  fatherName?: string;
  motherName?: string;
  /** Primary phone from the family registration form (single field on the form). */
  familyPhone?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  parentAccountName: string;
  parentAccountEmail?: string;
  parentAccountPhone?: string;
  /** Whether the family completed registration for this school year. */
  registrationCompleted: boolean;
}

/** GET /reports/emergency-contacts?semesterId= */
export interface ReportEmergencyContactsResponse {
  semesterId: string;
  semesterName: string;
  schoolYear: string;
  totalStudents: number;
  students: ReportEmergencyContactRow[];
}

/** GET /reports/enrollment-by-grade?semesterId= */
export interface ReportEnrollmentByGradeResponse {
  semesterId: string;
  semesterName?: string;
  totalStudents: number;
  buckets: EnrollmentByGradeBucket[];
}

/** One class row for GET /reports/class-capacity */
export interface ReportClassCapacityRow {
  classId: string;
  classTitle: string;
  teacherName: string;
  taken: number;
  maxStudents: number;
  left: number | null;
  percentFull: number | null;
  waitlistCount: number;
  waitlistEnabled: boolean;
}

export interface ReportClassCapacityTotals {
  taken: number;
  maxSeats: number;
  left: number;
  percentFull: number;
  waitlistCount: number;
  classCount: number;
  uncappedClassCount: number;
}

/** GET /reports/class-capacity?semesterId= */
export interface ReportClassCapacityResponse {
  semesterId: string;
  semesterName?: string;
  totals: ReportClassCapacityTotals;
  rows: ReportClassCapacityRow[];
}

/** GET /dashboard/class-capacity?semesterId= */
export interface DashboardClassCapacityResponse {
  semesterId: string;
  semesterName?: string;
  totals: ReportClassCapacityTotals;
  displayValue: string;
}

/**
 * Totals for GET /dashboard/registration-fees.
 * `totalFamilies` is enrolled registered families (same set as Families enrolled), not all registrations.
 */
export interface DashboardRegistrationFeeTotals {
  totalFamilies: number;
  yearlyFeePaidCount: number;
  yearlyFeeUnpaidCount: number;
  /** 0–100, rounded. */
  percentPaid: number;
  feeCents: number;
  /** Remaining due for paid enrolled families (cents). */
  collectedCents: number;
}

/** GET /dashboard/registration-fees?schoolYear= */
export interface DashboardRegistrationFeesResponse {
  schoolYear: string;
  totals: DashboardRegistrationFeeTotals;
  displayValue: string;
}

/** GET /dashboard/family-overview — registered families vs enrolled-among-registered */
export interface DashboardFamilyOverviewResponse {
  schoolYear: string;
  semesterId?: string;
  semesterName?: string;
  /** Completed family registrations (non-deleted, non-exempt). */
  registeredFamilies: number;
  /** Registered families with ≥1 enrollment in an approved class this semester. */
  enrolledAmongRegistered: number;
  /** 0–100, rounded. */
  percentEnrolled: number;
  /** e.g. "24/29 enrolled" */
  displayValue: string;
}

/** One enrolled student with tutor-facing notes for GET /reports/children-tutor-notes */
export interface ReportChildrenTutorNotesRow {
  childId: string;
  parentUserId: string;
  studentName: string;
  gradeLevel: string;
  classTitles: string[];
  specialComments: string;
  allergies?: string;
  parentAccountName: string;
  parentAccountEmail?: string;
}

/** GET /reports/children-tutor-notes?semesterId=&classId= */
export interface ReportChildrenTutorNotesResponse {
  semesterId: string;
  semesterName: string;
  schoolYear: string;
  totalStudents: number;
  students: ReportChildrenTutorNotesRow[];
}

/** GET /reports/registration-completion?schoolYear= */
export interface ReportRegistrationCompletionRow {
  userId: string;
  familyName: string;
  parentName: string;
  email: string;
  registrationComplete: boolean;
  completedAt?: number;
  yearlyFeePaid: boolean;
  paidAt?: number;
  paymentMethod?: string;
  feeDiscountApplied?: boolean;
  feeDiscountCents?: number;
}

export interface ReportRegistrationCompletionResponse {
  schoolYear: string;
  totalFamilies: number;
  completedCount: number;
  incompleteCount: number;
  yearlyFeePaidCount: number;
  yearlyFeeUnpaidCount: number;
  families: ReportRegistrationCompletionRow[];
}

/** Per-class rollup for GET /reports/attendance-rate-summary */
export interface ReportAttendanceRateClassRow {
  classId: string;
  classTitle: string;
  sessionsWithRecords: number;
  totalPresent: number;
  totalAbsent: number;
  /** e.g. "87.5%" or "—" */
  attendanceRate: string;
}

/** GET /reports/attendance-rate-summary?semesterId=&classId= */
export interface ReportAttendanceRateSummaryResponse {
  semesterId: string;
  semesterName?: string;
  classId?: string;
  classTitle?: string;
  rows: ReportAttendanceRateClassRow[];
  /** Present when classId is set — session-level detail */
  sessionBuckets?: ReportAttendanceSessionBucket[];
}

/** GET /reports/outstanding-payments?semesterId=&classId= */
export interface ReportOutstandingPaymentRow {
  enrollmentId: string;
  classId: string;
  classTitle: string;
  childId: string;
  studentName: string;
  familyName: string;
  parentName: string;
  classFee: number | null;
  amountReceived: number;
  balanceDue: number | null;
  paymentStatus: string;
}

export interface ReportOutstandingPaymentsResponse {
  semesterId: string;
  semesterName?: string;
  totalRows: number;
  rows: ReportOutstandingPaymentRow[];
}

/** GET /reports/semester-money?semesterId= — leadership money-in rollup */
export interface ReportSemesterMoneyClassRow {
  classId: string;
  classTitle: string;
  teacherId?: string;
  enrollmentCount: number;
  expectedTotal: number;
  collectedTotal: number;
  outstandingTotal: number;
}

export interface ReportSemesterMoneyTutorRow {
  teacherId?: string;
  tutorName: string;
  classCount: number;
  expectedTotal: number;
  collectedTotal: number;
  outstandingTotal: number;
}

export interface ReportSemesterMoneyFamilyRow {
  parentUserId: string;
  familyName: string;
  parentName: string;
  registrationFeePaid: boolean;
  /** Remaining yearly fee in cents (after discount); 0 when exempt. */
  registrationFeeCents: number;
  /** Amount taken off the yearly fee, in cents. */
  registrationDiscountCents?: number;
  classExpected: number;
  classCollected: number;
  classOutstanding: number;
}

export interface ReportSemesterMoneyResponse {
  semesterId: string;
  semesterName: string;
  schoolYear: string;
  familyRegistration: {
    feeCents: number;
    totalFamilies: number;
    yearlyFeePaidCount: number;
    yearlyFeeUnpaidCount: number;
    /** Full site fee × families (before discounts). */
    listCents: number;
    /** Sum of discounts applied. */
    discountCents: number;
    /** Remaining due for all families. */
    expectedCents: number;
    collectedCents: number;
    /** Remaining due for unpaid families. */
    outstandingCents: number;
  };
  classTuition: {
    enrollmentCount: number;
    expectedTotal: number;
    collectedTotal: number;
    outstandingTotal: number;
    paidInFullCount: number;
    partialCount: number;
    unpaidCount: number;
  };
  /** Registration collected (dollars) + class collected. */
  grandCollectedDollars: number;
  byClass: ReportSemesterMoneyClassRow[];
  byTutor: ReportSemesterMoneyTutorRow[];
  byFamily: ReportSemesterMoneyFamilyRow[];
}

/** One semester row for GET /reports/families-per-semester */
export interface ReportFamiliesPerSemesterRow {
  semesterId: string;
  semesterName: string;
  schoolYear?: string;
  status: SemesterStatus;
  enrolledFamilies: number;
  enrolledStudents: number;
}

/** GET /reports/families-per-semester — includes archived semesters */
export interface ReportFamiliesPerSemesterResponse {
  semesters: ReportFamiliesPerSemesterRow[];
}

export interface ReportGoogleContactsExportRow {
  userId: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
}

export interface GoogleContactsExportRoleOption {
  value: string;
  label: string;
}

export type GoogleContactsPopulation =
  | 'my-classes'
  | 'enrolled'
  | 'family-registrations'
  | 'all-users';

export interface GoogleContactsExportPopulationOption {
  value: GoogleContactsPopulation;
  label: string;
}

export interface GoogleContactsExportCapabilities {
  selectableRoles: GoogleContactsExportRoleOption[];
  selectablePopulations: GoogleContactsExportPopulationOption[];
  /** Leadership may pick family registrations or all user accounts. */
  canSelectPopulation: boolean;
  /** @deprecated Same as canSelectPopulation. Kept for older clients. */
  canSelectAllTime: boolean;
  canSelectMultipleSemesters: boolean;
  /** True when results are limited to the caller's own classes. */
  isTutorScoped: boolean;
}

/** GET /reports/google-contacts-export */
export interface ReportGoogleContactsExportResponse {
  population: GoogleContactsPopulation;
  /** all-time when population is all-users; otherwise semester. */
  scope: 'semester' | 'all-time';
  roles: string[];
  semesterIds: string[];
  semesterNames: string[];
  schoolYears: string[];
  /** Present when My classes is filtered to one class. */
  classId?: string;
  classTitle?: string;
  contacts: ReportGoogleContactsExportRow[];
  skippedNoEmail: number;
  capabilities: GoogleContactsExportCapabilities;
}

/** One month bucket for GET /reports/new-accounts-by-month */
export interface ReportNewAccountsMonthBucket {
  monthKey: string;
  label: string;
  count: number;
}

/** GET /reports/new-accounts-by-month?months= */
export interface ReportNewAccountsByMonthResponse {
  months: number;
  buckets: ReportNewAccountsMonthBucket[];
  totalInRange: number;
}

export type ReportLoginVisitsGranularity = 'daily' | 'weekly' | 'monthly' | 'yearly';

/** GET /reports/login-visits?audience= */
export type ReportLoginVisitsAudience =
  | 'all'
  | 'parents-tutors'
  | 'parents'
  | 'tutors'
  | 'leadership';

export interface ReportLoginVisitsBucket {
  bucketKey: string;
  label: string;
  count: number;
}

/** GET /reports/login-visits */
export interface ReportLoginVisitsResponse {
  granularity: ReportLoginVisitsGranularity;
  audience: ReportLoginVisitsAudience;
  audienceLabel: string;
  startDate: string;
  endDate: string;
  /** IANA timezone used for day boundaries (America/New_York). */
  timezone: string;
  buckets: ReportLoginVisitsBucket[];
  totalInRange: number;
}

/** GET /users/:userId/member-detail — children + all family registrations (admin). */
export interface UserMemberDetailResponse {
  children: Child[];
  registrations: FamilyRegistration[];
}
